import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  MapPin, 
  Trophy, 
  Lightbulb, 
  Zap,
  Target,
  Layers,
  ArrowUpRight,
  Sparkles,
  CheckCircle2,
  TrendingUp
} from 'lucide-react';
import { Container } from '../components/layout/container';
import { getWorkExperienceById, getWorkExperienceData } from '../data/workExperience';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const WorkExperienceDetailPage = () => {
  const { id } = useParams();
  const { language, t } = useLanguage();
  const experience = getWorkExperienceById(id, language);
  
  // Get adjacent experiences for navigation
  const allExperiences = getWorkExperienceData(language);
  const currentIndex = allExperiences.findIndex(exp => exp.id === id);
  const nextExperience = allExperiences[currentIndex + 1];
  const prevExperience = allExperiences[currentIndex - 1];

  if (!experience) {
    return (
      <div className="min-h-screen bg-[#0B0B0F]">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-red-500/5 rounded-full blur-[120px]" />
        </div>
        <div className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0B0B0F]/60 backdrop-blur-2xl">
          <Container>
            <div className="flex items-center justify-between h-16">
              <Link to="/experience" className="group flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-150">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors">
                  <ArrowLeft className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium">{t.experienceDetail?.back || 'Back'}</span>
              </Link>
            </div>
          </Container>
        </div>
        <section className="pt-32 pb-16">
          <Container>
            <div className="text-center max-w-md mx-auto">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
                <Sparkles className="h-10 w-10 text-white/20" />
              </div>
              <h1 className="text-2xl font-semibold text-white mb-3">
                {t.experienceDetail?.notFoundTitle || 'Experience Not Found'}
              </h1>
              <p className="text-white/50 mb-8">
                {t.experienceDetail?.notFoundDesc || "The work experience you're looking for doesn't exist or has been moved."}
              </p>
              <Link
                to="/experience"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0B0B0F] font-medium rounded-xl hover:bg-white/90 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                {t.experienceDetail?.viewAllExperiences || 'View All Experiences'}
              </Link>
            </div>
          </Container>
        </section>
      </div>
    );
  }

  const {
    companyDisplay,
    title,
    location,
    duration,
    status,
    summary,
    tags,
    metrics,
    overview,
    achievements,
    techStack,
    businessOutcomes,
    challenges
  } = experience;

  return (
    <div className="min-h-screen bg-[#0B0B0F] overflow-hidden">
      <SEO 
        title={`${companyDisplay} - ${title} | Nandan Priyadarshi`}
        description={summary}
        canonicalUrl={`/experience/${id}`}
        keywords={`${companyDisplay}, ${title}, ${tags.join(', ')}, Product Manager Experience`}
        ogType="article"
      />
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-slate-500/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[120px]" />
      </div>

      {/* Navigation */}

      {/* Hero Section */}
      <section className="relative pt-12 pb-12">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12">
              {/* Left Column - Main Info */}
              <div>
                {/* Company & Status */}
                <motion.div
                  className="flex flex-wrap items-center gap-4 mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white">
                    {companyDisplay}
                  </h1>
                  {status === 'current' && (
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 rounded-full border border-emerald-500/30">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                      </span>
                      {t.experienceDetail?.currentRole || 'Current Role'}
                    </span>
                  )}
                </motion.div>

                {/* Title */}
                <motion.h2
                  className="text-xl sm:text-2xl font-medium text-white/60 mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  {title}
                </motion.h2>

                {/* Meta Info */}
                <motion.div
                  className="flex flex-wrap items-center gap-6 mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-white/70">{location}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-white/70">{duration}</span>
                  </div>
                </motion.div>

                {/* Summary */}
                <motion.p
                  className="text-lg text-white/50 leading-relaxed mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  {summary}
                </motion.p>

                {/* Tags */}
                <motion.div
                  className="flex flex-wrap gap-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium bg-white/[0.03] text-white/50 rounded-lg border border-white/[0.08] hover:bg-white/[0.06] hover:text-white/70 transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </motion.div>
              </div>

              {/* Right Column - Metrics Card */}
              <motion.div
                className="lg:sticky lg:top-24"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
              >
                <div className="relative group">
                  {/* Card glow */}
                  <div className="absolute -inset-px rounded-3xl bg-gradient-to-b from-white/20 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
                  
                  <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-xl overflow-hidden">
                    {/* Top accent */}
                    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />
                    
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                          <TrendingUp className="h-5 w-5 text-emerald-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">
                            {t.experienceDetail?.keyImpact || 'Key Impact'}
                          </h3>
                          <p className="text-xs text-white/40">
                            {t.experienceDetail?.measurableOutcomes || 'Measurable outcomes'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        {metrics.map((metric, index) => (
                          <motion.div
                            key={index}
                            className="group/item relative p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-white/10 transition-[background-color,border-color] duration-200"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 + index * 0.1 }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-400/20 to-slate-600/10 flex items-center justify-center">
                                <Zap className="h-4 w-4 text-slate-300" />
                              </div>
                              <span className="text-sm font-medium text-white/80">{metric}</span>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </Container>
      </section>

      {/* Divider */}
      <div className="relative py-4">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
        </Container>
      </div>

      {/* Content Sections */}
      <section className="py-16">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12">
              {/* Main Content */}
              <div className="space-y-16">
                {/* Overview */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                      <Target className="h-5 w-5 text-white/60" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      {t.experienceDetail?.overview || 'Overview'}
                    </h3>
                  </div>
                  <div className="relative pl-6 border-l-2 border-white/10">
                    <p className="text-white/50 leading-relaxed text-lg">{overview}</p>
                  </div>
                </motion.div>

                {/* Key Achievements */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                      <Trophy className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">
                        {t.experienceDetail?.keyAchievements || 'Key Achievements'}
                      </h3>
                      <p className="text-sm text-white/40">
                        {t.experienceDetail?.impactDelivered || 'Impact delivered'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {achievements.map((achievement, index) => (
                      <motion.div
                        key={index}
                        className="group relative"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex gap-4 p-5 rounded-2xl bg-gradient-to-r from-white/[0.04] to-transparent border border-white/[0.06] hover:border-white/10 hover:bg-white/[0.06] transition-[background-color,border-color] duration-200">
                          <div className="shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400/20 to-amber-600/10 flex items-center justify-center">
                            <span className="text-sm font-bold text-amber-300">{index + 1}</span>
                          </div>
                          <p className="text-white/60 leading-relaxed pt-1">{achievement}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Tech Stack */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                      <Layers className="h-5 w-5 text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">
                        {t.experienceDetail?.techStack || 'Tech Stack & Systems'}
                      </h3>
                      <p className="text-sm text-white/40">
                        {t.experienceDetail?.toolsUsed || 'Tools & technologies used'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-3">
                    {techStack.map((tech, index) => (
                      <motion.span
                        key={index}
                        className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-indigo-500/20 to-indigo-600/10 text-indigo-200 rounded-xl border border-indigo-500/20 hover:border-indigo-400/30 transition-colors"
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ scale: 1.05 }}
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                </motion.div>

                {/* Challenges */}
                {challenges && challenges.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                        <Lightbulb className="h-5 w-5 text-orange-400" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">
                          {t.experienceDetail?.challengesTackled || 'Challenges Tackled'}
                        </h3>
                        <p className="text-sm text-white/40">
                          {t.experienceDetail?.problemsSolved || 'Problems solved'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid gap-3">
                      {challenges.map((challenge, index) => (
                        <motion.div
                          key={index}
                          className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] transition-colors"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <CheckCircle2 className="h-5 w-5 text-orange-400 mt-0.5 shrink-0" />
                          <span className="text-white/50">{challenge}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Sidebar - Business Outcomes */}
              <div className="lg:sticky lg:top-24 space-y-6 h-fit">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="relative group"
                >
                  <div className="absolute -inset-px rounded-3xl bg-gradient-to-b from-emerald-500/20 via-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
                  
                  <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-transparent backdrop-blur-xl overflow-hidden">
                    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent" />
                    
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                          <Trophy className="h-5 w-5 text-emerald-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">
                            {t.experienceDetail?.businessOutcomes || 'Business Outcomes'}
                          </h3>
                          <p className="text-xs text-white/40">
                            {t.experienceDetail?.resultsDelivered || 'Results delivered'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        {businessOutcomes.map((outcome, index) => (
                          <motion.div
                            key={index}
                            className="flex items-center gap-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20"
                            initial={{ opacity: 0, x: 10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <div className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span className="text-sm font-medium text-emerald-200">{outcome}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Navigation Footer */}
      <section className="py-12 border-t border-white/5">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row items-stretch gap-4">
              {prevExperience && (
                <Link
                  to={`/experience/${prevExperience.id}`}
                  className="group flex-1 p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-[background-color,border-color] duration-200"
                >
                  <div className="flex items-center gap-2 text-sm text-white/40 mb-2">
                    <ArrowLeft className="h-4 w-4" />
                    {t.experienceDetail?.previous || 'Previous'}
                  </div>
                  <div className="text-lg font-semibold text-white group-hover:text-white/90 transition-colors">
                    {prevExperience.companyDisplay}
                  </div>
                  <div className="text-sm text-white/50 truncate">{prevExperience.title}</div>
                </Link>
              )}
              
              {nextExperience && (
                <Link
                  to={`/experience/${nextExperience.id}`}
                  className="group flex-1 p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-[background-color,border-color] duration-200 text-right"
                >
                  <div className="flex items-center justify-end gap-2 text-sm text-white/40 mb-2">
                    {t.experienceDetail?.next || 'Next'}
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                  <div className="text-lg font-semibold text-white group-hover:text-white/90 transition-colors">
                    {nextExperience.companyDisplay}
                  </div>
                  <div className="text-sm text-white/50 truncate">{nextExperience.title}</div>
                </Link>
              )}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default WorkExperienceDetailPage;
